import { useState, useRef, useMemo } from 'react'
import { useMutation } from 'react-query'
import { getPeopleMergePreview } from 'services/people'
import { sortJobsAccordingToPrimary } from '../utils'

function usePeopleMergePreview({ onPersonsChange } = {}) {
  const [sourceId, setSourceId] = useState('')
  const [targetId, setTargetId] = useState('')
  const lastSearchedIds = useRef({ sourceId: null, targetId: null })

  const {
    data: mergePreviewData,
    mutate: getPreviewData,
    isLoading,
    isError,
    error,
    reset,
  } = useMutation(({ sourceId, targetId }) => getPeopleMergePreview(sourceId, targetId), {
    retry: false,
  })

  const handleSearch = () => {
    if (sourceId && targetId) {
      const idsChanged =
        lastSearchedIds.current.sourceId !== sourceId ||
        lastSearchedIds.current.targetId !== targetId

      if (idsChanged && onPersonsChange) {
        onPersonsChange()
      }

      lastSearchedIds.current = { sourceId, targetId }
      getPreviewData({ sourceId, targetId })
    }
  }

  const handleSourceIdChange = value => {
    setSourceId(value)
    if (isError) {
      reset()
    }
  }

  const handleTargetIdChange = value => {
    setTargetId(value)
    if (isError) {
      reset()
    }
  }

  const getFieldErrors = isError => {
    if (!isError) {
      return {
        sourceError: null,
        targetError: null,
      }
    }

    const errors = error?.response?.data?.errors || []
    const sourceError = errors.find(err => err.field === 'source_id')
    const targetError = errors.find(err => err.field === 'target_id')

    return {
      sourceError: sourceError?.message || null,
      targetError: targetError?.message || null,
    }
  }

  const { sourceError, targetError } = getFieldErrors(isError)

  const { sourcePerson, targetPerson, sourceJobs, targetJobs } = useMemo(() => {
    let sourcePersonDefault = null
    let targetPersonDefault = null

    mergePreviewData?.data?.forEach(person => {
      if (person.id === sourceId) {
        sourcePersonDefault = {
          ...person,
          jobs: sortJobsAccordingToPrimary(person.jobs),
        }
      }
      if (person.id === targetId) {
        targetPersonDefault = {
          ...person,
          jobs: sortJobsAccordingToPrimary(person.jobs),
        }
      }
    })
    return {
      sourcePerson: sourcePersonDefault,
      targetPerson: targetPersonDefault,
      sourceJobs: sourcePersonDefault?.jobs || [],
      targetJobs: targetPersonDefault?.jobs || [],
    }
  }, [mergePreviewData, sourceId, targetId])

  const preview = mergePreviewData?.meta?.preview
  const previewJobsMap = new Map(
    preview?.sourceJobs?.concat(preview?.targetJobs)?.map(job => [job.entityId, job]),
  )

  return {
    sourceId,
    targetId,
    handleSourceIdChange,
    handleTargetIdChange,
    sourcePerson,
    targetPerson,
    sourceJobs,
    targetJobs,
    preview,
    previewJobsMap,
    handleSearch,
    isLoading,
    isError,
    error,
    sourceError,
    targetError,
  }
}

export default usePeopleMergePreview
